import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  DOCUMENT,
  DOCUMENT_NAV_LOGO_HEIGHT_MOBILE,
  documentNavLogoSx,
  pageShellSx,
} from '../constants/documentTheme';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import usePartnerMobileLayout from '../hooks/usePartnerMobileLayout';
import { PARTNER_CHROME_HEIGHT, PARTNER_SIDEBAR_WIDTH } from '../utils/partnerWorkspace';
import {
  applyBrandCssVars,
  cssVarsFromWorkspaceBrand,
  getPartnerWorkspaceBrand,
} from '../utils/partnerWorkspaceBrand';
import { developerPublicBase } from '../utils/developerHost';
import { tryRegisterPartnerPushDevice } from '../utils/partnerPush';
import PartnerLogoMark from './PartnerLogoMark';
import PartnerPlanBadge from './PartnerPlanBadge';
import PartnerMobileBottomNav from './PartnerMobileBottomNav';
import PartnerDesktopSidebar from './PartnerDesktopSidebar';
import WhatsNewBanner from './WhatsNewBanner';

const PartnerChrome = ({ partner, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = usePartnerMobileLayout();
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const { entitlements } = usePartnerEntitlements();
  const brand = getPartnerWorkspaceBrand(partner, entitlements);
  const headerBg = brand?.banner || DOCUMENT.oxblood;
  const accent = brand?.accent || DOCUMENT.navAccent;
  const menuInk = brand?.navInk || DOCUMENT.navInk;
  const companyLogoUrl = brand?.logoUrl
    || (entitlements?.tier === 'bespoke' && isUsableImageUrl(partner?.logo)
      ? String(partner.logo).trim()
      : null);
  const houseLogoFailed = logoFailed && !companyLogoUrl && !brand;

  useEffect(() => {
    applyBrandCssVars(cssVarsFromWorkspaceBrand(brand));
    return () => applyBrandCssVars(null);
  }, [brand]);

  useEffect(() => {
    tryRegisterPartnerPushDevice(api).catch(() => {});
  }, []);

  return (
    <>
      <PartnerDesktopSidebar
        partner={partner}
        brand={brand}
        entitlements={entitlements}
        headerBg={headerBg}
        accent={accent}
        menuInk={menuInk}
        logoFailed={logoFailed}
        onLogoError={() => setLogoFailed(true)}
      />

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          flexShrink: 0,
          display: { xs: 'block', md: 'none' },
        }}
        data-testid="partner-chrome"
      >
        {brand || !isMobile ? null : <WhatsNewBanner />}
        <Box sx={{ height: PARTNER_CHROME_HEIGHT.xs }}>
          <Box sx={{ bgcolor: headerBg, height: '100%', borderBottom: `1px solid ${brand ? 'rgba(255,255,255,0.12)' : DOCUMENT.navRule}` }}>
            <Box
              sx={{
                ...pageShellSx,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                component={RouterLink}
                to="/partner/dashboard"
                aria-label={brand?.name || 'Déjà Vu Nomad'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  flexShrink: 0,
                  minWidth: 0,
                }}
              >
                {companyLogoUrl ? (
                  <Box
                    component="img"
                    src={companyLogoUrl}
                    alt={brand?.name || partner?.company_name || ''}
                    data-testid="partner-workspace-logo"
                    sx={{
                      height: DOCUMENT_NAV_LOGO_HEIGHT_MOBILE - 12,
                      width: 'auto',
                      maxWidth: 148,
                      objectFit: 'contain',
                      objectPosition: 'left center',
                      display: 'block',
                      my: 0.5,
                    }}
                  />
                ) : brand ? (
                  <Typography
                    sx={{
                      fontFamily: DOCUMENT.sans,
                      fontSize: 12,
                      color: DOCUMENT.ivory,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      maxWidth: 148,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {brand.name}
                  </Typography>
                ) : houseLogoFailed ? (
                  <Typography
                    sx={{
                      fontFamily: DOCUMENT.display,
                      fontSize: 16,
                      color: DOCUMENT.ivory,
                      letterSpacing: '0.04em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Déjà Vu Nomad
                  </Typography>
                ) : (
                  <Box
                    component="img"
                    src="/noBgWhite.png"
                    alt="Déjà Vu Nomad"
                    loading="eager"
                    decoding="sync"
                    onError={() => setLogoFailed(true)}
                    sx={{
                      ...documentNavLogoSx,
                      height: DOCUMENT_NAV_LOGO_HEIGHT_MOBILE,
                      maxWidth: 148,
                    }}
                  />
                )}
              </Box>

              <Box
                component="button"
                aria-label={t('nav.profile')}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  p: 0,
                  border: `1px solid ${accent}`,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: DOCUMENT.ivory,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  fontFamily: DOCUMENT.display,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PartnerLogoMark
                  partner={partner}
                  src={partner?.avatar || ''}
                  alt={partner?.contact_name || partner?.company_name || ''}
                  initialsFrom="contact"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Desktop top strip: avatar + plan only (nav lives in left rail) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          top: 0,
          zIndex: 20,
          height: PARTNER_CHROME_HEIGHT.md,
          bgcolor: headerBg,
          borderBottom: `1px solid ${brand ? 'rgba(255,255,255,0.12)' : DOCUMENT.navRule}`,
          pl: `${PARTNER_SIDEBAR_WIDTH}px`,
        }}
        data-testid="partner-chrome-desktop"
      >
        <Box
          sx={{
            ...pageShellSx,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <PartnerPlanBadge
            entitlements={entitlements}
            variant="nav"
            href="/partner/billing"
          />
          <Box
            component="button"
            aria-label={t('nav.profile')}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              p: 0,
              border: `1px solid ${accent}`,
              bgcolor: 'rgba(255,255,255,0.1)',
              color: DOCUMENT.ivory,
              overflow: 'hidden',
              cursor: 'pointer',
              fontFamily: DOCUMENT.display,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PartnerLogoMark
              partner={partner}
              src={partner?.avatar || ''}
              alt={partner?.contact_name || partner?.company_name || ''}
              initialsFrom="contact"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/partner/how-to'); }}>
          {t('howTo.menu')}
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/partner/profile'); }}>
          {t('partnerDash.profile')}
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/partner/billing'); }}>
          {t('nav.partnerBilling')}
        </MenuItem>
        {!isMobile && (
          <MenuItem
            component="a"
            href={developerPublicBase()}
            onClick={() => setAnchorEl(null)}
          >
            {t('partnerDash.developer')}
          </MenuItem>
        )}
        <MenuItem onClick={() => { setAnchorEl(null); onLogout?.(); }}>
          {t('partnerDash.signOut')}
        </MenuItem>
      </Menu>

      <PartnerMobileBottomNav
        accent={accent}
        ink={menuInk}
        banner={headerBg}
      />
    </>
  );
};

export default PartnerChrome;
